'use client';
import React, { useEffect, useState } from "react";
import Button from "@/components/atoms/button";
import Slider from "@/components/atoms/slider";
import Typography from "@/components/atoms/typography";
import {
  desktop,
  hugeDesktop,
  largeDesktop,
  mediumDesktop,
  mobile,
  smallMobile,
  tablet,
} from "@/helpers/utils/screensizes";
import useMediaQuery from "@/hooks/useMediaQuery";
import useTouchDevice from "@/hooks/useTouchDevice";
import { breakpoints } from "@/helpers/utils/breakpoints";
import Link from "next/link";
import Image from '@/components/atoms/Image';
import { sdk } from "@/sdk/CommercetoolsSDK";


interface VideoItem {
  idVideo: string;
  idItem: string;
  videoName: string;
  videoDuration: string;
  videoType: string;
  // Add other properties as needed
}

interface VideoItem {
  idProduct: string;
  idItem: string;
  category: string;
  idVideo: string;
  videoName: string;
  videoDuration: string;
  videoType: string;
  videoThumbnail: string;
  // Add any other properties that might exist on the video object
  [key: string]: any;
}

interface TopVideosSectionProps {
  data?: {
    videoList: VideoItem[];
    // Add any other properties that might exist on the data object
    [key: string]: any;
  };
}

export const TopVideosSection = () => {
  const [isMobile] = useMediaQuery(mobile);
  const { isTouchDevice } = useTouchDevice();
  const [data, setData] = useState<TopVideosSectionProps['data']>();
  const [loading, setLoading] = useState(true);
  const extensions = sdk.composableCommerce;
  const truncatedTitle = (title: string) => {
    return !isMobile ? (title.substring(0, 23) + "..") : title;
  }

  useEffect(() => {
    async function fetchVideoList() {
      const regex = /Calling extension(?: \S+)? failed/;
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second delay between retries

      try {
        setLoading(true);

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const response = await extensions.custom.videoList({});
            // Update the data state with the response
            if (response?.data?.data) {
              setData(response?.data?.data);
            }
            break; // Success - exit retry loop
          } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || '';

            // Check if error matches the retry pattern
            if (regex.test(errorMessage) && attempt < maxRetries - 1) {
              console.warn(`Attempt ${attempt + 1} failed with extension error. Retrying...`);
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
              continue;
            }

            // If it's not a retryable error or we've exhausted retries, throw
            throw error;
          }
        }
      } catch (error) {
        console.error('Error fetching video list:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchVideoList();
  }, []);

  // Remove duplicates and shuffle videoList without mutating the original data
  const shuffledVideos = React.useMemo(() => {
    // First remove duplicates based on idVideo
    const uniqueVideos = Array.from(new Map(
      (data?.videoList || []).map((video: VideoItem) => [video.idVideo, video])
    ).values());

    // Then shuffle the unique videos using Fisher-Yates
    const list = [...uniqueVideos];
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }

    return list;
  }, [data?.videoList]);

  return (
    <div className="bg-[#2B2C39]">
      <style jsx global>{`
        .custom-slider .swiper-pagination {
          bottom: 30px !important;
          position: absolute;
          z-index: 10;
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .custom-slider .swiper-pagination-bullet {
          width: 10px;
          height: 10px;
          background: #fff;
          margin: 0 5px;
        }

        .custom-slider .swiper-pagination-bullet-active {
          opacity: 1;
          background: #46c1a9;
        }

        @media (min-width: 480px) and (max-width: 767px) {
          .custom-slider .swiper-pagination {
            bottom: 40px !important;
          }
        }
      `}</style>
      <div className="lg:px-[93px]  px-[43px] py-6 ">
        <h1 className="text-[20px] lg:text-36 font-semibold text-[#ffffff] pb-4">Recommended Videos</h1>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="88px"
              height="88px"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid"
            >
              <circle
                cx="50"
                cy="50"
                r="32"
                strokeWidth="8"
                stroke="#090909"
                strokeDasharray="50.26548245743669 50.26548245743669"
                fill="none"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  dur="1s"
                  repeatCount="indefinite"
                  keyTimes="0;1"
                  values="0 50 50;360 50 50"
                ></animateTransform>
              </circle>
              <circle
                cx="50"
                cy="50"
                r="23"
                strokeWidth="8"
                stroke="#090909"
                strokeDasharray="36.12831551628262 36.12831551628262"
                strokeDashoffset="36.12831551628262"
                fill="none"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  dur="1s"
                  repeatCount="indefinite"
                  keyTimes="0;1"
                  values="0 50 50;-360 50 50"
                ></animateTransform>
              </circle>
            </svg>
          </div>
        ) : (
          <div className="relative w-full">
          <Slider
            dots={true}
            autoplay={{
              pauseOnMouseEnter: true,
              delay: 5000,
              disableOnInteraction: false,
            }}
            allowTouchMove={isTouchDevice}
            nextButtonStyles={{ transform: "translateY(-150%)", right: "10px", color: "#ffffff" }}
            prevButtonStyles={{ transform: "translateY(-150%)", left: "10px", color: "#ffffff" }}
            slidesPerView={2.68}
            spaceBetween={16}
            breakpoints={{
              [smallMobile]: {
                slidesPerView: 1.5,
                spaceBetween: 12,
                ...(breakpoints[smallMobile] ?? {}),
              },
              [mobile]: {
                slidesPerView: 1.4,
                spaceBetween: 12,
                ...(breakpoints[mobile] ?? {}),
              },
              [tablet]: {
                slidesPerView: 1.6,
                spaceBetween: 16,
                ...(breakpoints[tablet] ?? {}),
              },
              [desktop]: {
                slidesPerView: 2.5,
                spaceBetween: 16,
                ...(breakpoints[desktop] ?? {}),
              },
              [mediumDesktop]: {
                slidesPerView: 3.5,
                spaceBetween: 16,
                ...(breakpoints[mediumDesktop] ?? {}),
              },
              [largeDesktop]: {
                slidesPerView: 4,
                spaceBetween: 16,
                ...(breakpoints[largeDesktop] ?? {}),
              },
              [hugeDesktop]: {
                slidesPerView: 4.5,
                spaceBetween: 16,
                ...(breakpoints[hugeDesktop] ?? {}),
              },
            }}
            className="custom-slider !pb-[71px] sm:!pb-[90px] md:!pb-[71px]"
          >
            {shuffledVideos.slice(0, 5).map((video: any) => (
              <Link
                href={`/video-list/${video.idVideo}`}
                key={`${video.idVideo}-${video.idItem}`}
                className="block group relative bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="relative aspect-video bg-gray-100 rounded-[12px]">
                  <Image
                    priority
                    src={video.videoThumbnail}
                    alt={video.videoName ?? ''}
                    style={{ objectPosition: 'center' }}
                    className="w-full h-full !relative rounded-sm group-hover:opacity-75"
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                  <div className="absolute top-2 right-2 bg-[#48C9B080] bg-opacity-70 text-white text-xs px-[18px] py-[10px] rounded flex items-center">
                    <span className="text-16 font-bold">
                      {video.videoDuration.trim()} {video.videoDuration.trim() === '1' ? 'min' : 'mins'}
                    </span>
                  </div>
                </div>
                <div className="px-4 py-6 h-32">
                  <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">
                    {video.videoName}
                  </h3>
                  <div className="flex justify-between items-start">
                    <span className="text-12 font-medium text-gray-700 bg-[#ececec] px-2 py-1 rounded">
                      {video.videoType}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </Slider>
        </div>
        )}
      </div>
    </div>
  );
};
